import React from "react";
import {
  Menu,
  MenuProps,
  MenuItem,
  Button,
  Divider,
  useTheme,
  Typography,
  Box,
  Tooltip,
} from "@mui/material";
import { Star } from "lucide-react";
import DoneIcon from "@mui/icons-material/Done";
import {
  BodypartType,
  ExerciseAssetDto,
  MovementPatternType,
} from "@growth-machine-llc/stridist-api-client";
import { BodypartTypeLabels, MovementPatternTypeLabels } from "./constants";
import { CategoryType, CategoryTypeLabel } from "./ManageLibraryExercise";
import { isEqual } from "lodash";
import { exerciseTagCategoryMultiSelectStyles } from "./exerciseLibraryStyles";
import { UpdateExerciseFunctionType } from "./ExerciseCategoryBadge";

export interface ExerciseCategoryMultiSelectProps<T extends CategoryType>
  extends Omit<MenuProps, "open" | "onClose"> {
  label: CategoryTypeLabel;
  showButton?: boolean;
  onClose?: () => void;
  exercise?: ExerciseAssetDto;
  anchorEl?: HTMLElement | null;
  onChangeSelectedOptions?: (selected: T[]) => void;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  initialSelected?: T[];
  readonly?: boolean;
  updateExercise?: UpdateExerciseFunctionType;
  showOnlySelected?: boolean;
}

export function ExerciseCategoryMultiSelect<T extends CategoryType>(
  props: ExerciseCategoryMultiSelectProps<T>,
) {
  const theme = useTheme();
  const s = exerciseTagCategoryMultiSelectStyles();
  const {
    label,
    onChangeSelectedOptions,
    onClose,
    open,
    setOpen,
    anchorEl,
    showButton = true,
    initialSelected = [],
    exercise,
    readonly = false,
    updateExercise,
  } = props;

  const [selectedOptions, setSelectedOptions] =
    React.useState<T[]>(initialSelected);

  React.useEffect(() => {
    if (!isEqual(selectedOptions, initialSelected)) {
      setSelectedOptions(initialSelected);
    }
  }, [initialSelected]);

  const [localAnchorEl, setLocalAnchorEl] = React.useState<HTMLElement | null>(
    null,
  );
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const primaryItem = selectedOptions ? selectedOptions[0] : null;

  const options = Object.values(
    label === CategoryTypeLabel.Bodypart ? BodypartType : MovementPatternType,
  ).filter((option) => option !== ("NONE" as unknown as T));

  const isOpen = open !== undefined ? open : Boolean(localAnchorEl);

  const handleClose = () => {
    if (setOpen) {
      setOpen(false);
    } else {
      setLocalAnchorEl(null);
    }
    onClose?.();
    if (
      exercise &&
      !isEqual(selectedOptions, initialSelected) &&
      !readonly &&
      updateExercise
    ) {
      updateExercise({
        ...exercise,
        ...(label === CategoryTypeLabel.Bodypart
          ? {
              bodyparts: selectedOptions as BodypartType[],
            }
          : {
              movementPatterns: selectedOptions as MovementPatternType[],
            }),
      });
    }
  };

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (setOpen) {
      setOpen(true);
    } else {
      setLocalAnchorEl(event.currentTarget);
    }
  };

  const isSelected = (item: T) => selectedOptions?.includes(item);

  const toggleOption = (item: T) => {
    if (readonly) return;
    const isAlreadySelected = isSelected(item);
    if (isAlreadySelected) {
      const updated = selectedOptions.filter((o) => o !== item);
      setSelectedOptions(updated);
      onChangeSelectedOptions?.(updated);
    } else {
      if (selectedOptions.length >= 3) return;
      const updated = [...selectedOptions, item];
      setSelectedOptions(updated);
      onChangeSelectedOptions?.(updated);
    }
  };

  const setPrimary = (item: T) => {
    if (!isSelected(item)) return;
    const updated = [item, ...selectedOptions.filter((o) => o !== item)];
    setSelectedOptions(updated);
    onChangeSelectedOptions?.(updated);
  };

  return (
    <>
      {showButton && (
        <Button
          ref={buttonRef}
          className={s.manageButton}
          children={`Manage ${label.toLowerCase()}`}
          onClick={handleButtonClick}
        />
      )}
      <Menu
        open={isOpen}
        anchorEl={anchorEl || localAnchorEl || buttonRef.current}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              minWidth: 300,
              maxHeight: 200,
            },
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        className={s.menu}
        onClick={(e) => {
          if (readonly) {
            e.stopPropagation();
            return;
          }
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <MenuItem className={s.menuHeader}>
          {readonly ? label : "Select up to 3 types"}
        </MenuItem>
        <Divider />
        {options.map((option) => {
          const selected = isSelected(option);
          const isPrimary = primaryItem === option;

          return (
            <MenuItem
              key={String(option)}
              disabled={
                (selectedOptions.length >= 3 && !selected) ||
                (!selected && readonly)
              }
              className={s.menuItem}
              onClick={() => toggleOption(option)}
            >
              <Box display={"flex"} alignItems={"center"}>
                <Box
                  sx={{
                    width: 20,
                    display: "inline-flex",
                    justifyContent: "center",
                    mx: 1,
                  }}
                >
                  {selected && <DoneIcon color="primary" fontSize="small" />}
                </Box>

                {label === CategoryTypeLabel.Bodypart &&
                  BodypartTypeLabels[option as BodypartType]}
                {label === CategoryTypeLabel.MovementPattern &&
                  MovementPatternTypeLabels[option as MovementPatternType]}

                {selected && isPrimary && (
                  <Button
                    className={s.primaryLabel}
                    variant={"contained"}
                    children={"Primary"}
                  />
                )}
              </Box>
              {selected && (
                <Tooltip
                  title={!readonly ? "Set as primary" : null}
                  arrow
                  slotProps={{
                    popper: {
                      sx: {
                        zIndex: 999999,
                      },
                    },
                    tooltip: {
                      sx: {
                        px: 2,
                        py: 0.75,
                        fontSize: 12,
                      },
                    },
                  }}
                >
                  <Typography
                    component="button"
                    ml={2}
                    onClick={(e) => {
                      if (readonly) {
                        e.stopPropagation();
                        return;
                      }
                      e.stopPropagation();
                      setPrimary(option);
                    }}
                    children={
                      <Star
                        fill={
                          isPrimary
                            ? theme.palette.primary.main
                            : theme.palette.background.paper
                        }
                        color={theme.palette.primary.main}
                        size={20}
                      />
                    }
                  />
                </Tooltip>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
